/**
 * TaquillaPage — PWA fullscreen para taquilla en pagafacil.eu.
 *
 * Ruta: /operador/taquilla?t=<token>
 *
 * Requiere token de operador (magic link, mismo que /operador). Modos:
 *  - Escanear con la cámara (BarcodeDetector nativo o jsQR fallback).
 *  - Buscar por referencia · escribe la ref manual o pega el enlace del QR.
 *
 * Al pulsar "Validar" llama POST /operator/entradas/checkin y muestra
 * el estado actualizado (X / Y validadas). El backend garantiza que solo
 * se puedan validar cantidades disponibles.
 */
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../api/client';
import QrCameraScanner from '../components/QrCameraScanner';

/** Prompt de instalación del navegador (Chrome/Edge/Android/Samsung). */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface TicketState {
  id: string;
  order_reference: string;
  buyer_name: string;
  buyer_email: string;
  quantity: number;
  tickets_validated: number;
  status: string;
  show_title?: string | null;
  show_datetime?: string | null;
  venue_name?: string | null;
}

export default function TaquillaPage() {
  const [params] = useSearchParams();
  const token = params.get('t') || '';

  if (!token) {
    return <NoToken />;
  }

  return <TaquillaApp token={token} />;
}

function NoToken() {
  return (
    <div style={fullscreenStyle}>
      <div style={{ maxWidth: 480, textAlign: 'center', color: '#F7F2E9' }}>
        <div style={{ fontFamily: '"Spectral", serif', fontSize: 32, marginBottom: 12 }}>Sin token de operador</div>
        <div style={{ opacity: 0.7, fontSize: 15 }}>
          El acceso a la taquilla requiere el enlace de operador. Pide uno nuevo desde <b>/operador</b> y añádelo a esta URL.
        </div>
      </div>
    </div>
  );
}

function TaquillaApp({ token }: { token: string }) {
  const [input, setInput] = useState('');
  const [ticket, setTicket] = useState<TicketState | null>(null);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [history, setHistory] = useState<Array<{ ref: string; buyer: string; validated_now: number; ts: number }>>([]);
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showIosHint, setShowIosHint] = useState(false);

  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      setInstallPrompt(e as BeforeInstallPromptEvent);
    };
    window.addEventListener('beforeinstallprompt', handler);

    // iOS Safari no soporta beforeinstallprompt — detección para pintar
    // instrucciones "Añadir a pantalla de inicio" manualmente.
    const ua = navigator.userAgent;
    const isIos = /iPad|iPhone|iPod/.test(ua);
    // @ts-expect-error standalone es propiedad no-estándar en Safari iOS
    const alreadyStandalone = window.navigator.standalone === true || window.matchMedia('(display-mode: fullscreen)').matches || window.matchMedia('(display-mode: standalone)').matches;
    if (isIos && !alreadyStandalone && !sessionStorage.getItem('taquilla_ios_hint_dismissed')) {
      setShowIosHint(true);
    }

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const install = useCallback(async () => {
    if (!installPrompt) return;
    await installPrompt.prompt();
    const choice = await installPrompt.userChoice;
    if (choice.outcome === 'accepted') setInstallPrompt(null);
  }, [installPrompt]);

  const parseRef = (raw: string): string => {
    const s = raw.trim();
    if (!s) return '';
    // Si es una URL de validación (/api/public/entradas/validate/<token>), extraemos el token
    const m = s.match(/\/validate\/([A-Za-z0-9_-]+)/);
    return m ? m[1] : s;
  };

  const search = useCallback(async () => {
    const ref = parseRef(input);
    if (!ref) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.get<TicketState>(`/operator/entradas/lookup?reference=${encodeURIComponent(ref)}&t=${encodeURIComponent(token)}`);
      setTicket(r.data);
    } catch (e: any) {
      setTicket(null);
      setError(e?.response?.data?.detail || 'No encontrado');
    } finally {
      setBusy(false);
    }
  }, [input, token]);

  const checkin = useCallback(async (count: number) => {
    if (!ticket) return;
    const ref = ticket.order_reference;
    setBusy(true);
    setError(null);
    try {
      const r = await api.post<TicketState>(
        `/operator/entradas/checkin?t=${encodeURIComponent(token)}`,
        { reference_or_qr: ref, count }
      );
      setTicket(r.data);
      setHistory((h) => [
        { ref, buyer: r.data.buyer_name, validated_now: count, ts: Date.now() },
        ...h.slice(0, 9),
      ]);
      if (navigator.vibrate) navigator.vibrate([50, 30, 50]);
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Error al validar');
      if (navigator.vibrate) navigator.vibrate([120, 60, 120]);
    } finally {
      setBusy(false);
    }
  }, [ticket, token]);

  const reset = () => {
    setInput('');
    setTicket(null);
    setError(null);
  };

  return (
    <div style={fullscreenStyle}>
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#F7F2E9', padding: '12px 20px', background: 'rgba(255,255,255,0.03)' }}>
        <div style={{ fontFamily: '"Spectral", serif', fontSize: 18, fontWeight: 700, letterSpacing: 1 }}>🎟 TAQUILLA</div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {installPrompt && (
            <button onClick={() => void install()} style={btnInstall}>
              ↓ Instalar
            </button>
          )}
          {ticket && (
            <button onClick={reset} style={btnGhostSmall}>Nueva búsqueda</button>
          )}
        </div>
      </header>

      {showIosHint && (
        <div style={iosHintStyle}>
          <div style={{ flex: 1, fontSize: 13, lineHeight: 1.4 }}>
            <b>Instalar en el iPhone:</b> pulsa <span style={{ display: 'inline-block', border: '1px solid rgba(247,242,233,0.4)', borderRadius: 4, padding: '1px 6px', margin: '0 4px' }}>Compartir</span>
            <span style={{ opacity: 0.7 }}>→</span>
            &nbsp;<b>Añadir a pantalla de inicio</b>.
          </div>
          <button
            onClick={() => { sessionStorage.setItem('taquilla_ios_hint_dismissed', '1'); setShowIosHint(false); }}
            style={{ background: 'transparent', color: '#F7F2E9', border: 'none', fontSize: 20, cursor: 'pointer', padding: 4 }}
            aria-label="Cerrar"
          >×</button>
        </div>
      )}

      {!ticket ? (
        <main style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: 24, gap: 16, color: '#F7F2E9' }}>
          {/* Escáner cámara — botón grande arriba, es el flujo primario */}
          <button
            onClick={() => setScannerOpen(true)}
            style={{
              background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)',
              color: '#0A0A0A', border: 'none',
              padding: '28px 24px', borderRadius: 20,
              fontSize: 22, fontWeight: 900,
              cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
            }}
          >
            <span style={{ fontSize: 48 }}>📷</span>
            Escanear QR con cámara
          </button>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, opacity: 0.6, fontSize: 12 }}>
            <div style={{ flex: 1, height: 1, background: 'rgba(247,242,233,0.2)' }} />
            <span>o entrada manual</span>
            <div style={{ flex: 1, height: 1, background: 'rgba(247,242,233,0.2)' }} />
          </div>

          <div style={{ fontFamily: '"Space Mono", monospace', fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', opacity: 0.6 }}>Buscar por referencia</div>
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void search(); }}
            placeholder="SH-1234567890-AB12 o pega enlace del QR"
            style={{
              background: 'rgba(247,242,233,0.08)',
              border: '1px solid rgba(247,242,233,0.2)',
              color: '#F7F2E9',
              padding: '16px 18px',
              borderRadius: 14,
              fontSize: 18,
              fontFamily: '"Space Mono", monospace',
              width: '100%',
              boxSizing: 'border-box',
            }}
          />
          <button
            onClick={() => void search()} disabled={busy || !input.trim()}
            style={{
              background: busy || !input.trim() ? '#3D4A4F' : '#E8B44A',
              color: '#1E2A2E',
              border: 'none',
              padding: '16px 20px',
              borderRadius: 14,
              fontSize: 16,
              fontWeight: 700,
              cursor: busy || !input.trim() ? 'not-allowed' : 'pointer',
            }}
          >
            {busy ? 'Buscando…' : 'Buscar entrada'}
          </button>
          {error && <div style={errBox}>{error}</div>}

          {history.length > 0 && (
            <div style={{ marginTop: 20 }}>
              <div style={{ fontFamily: '"Space Mono", monospace', fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', opacity: 0.55, marginBottom: 8 }}>Recientes</div>
              <div style={{ display: 'grid', gap: 6 }}>
                {history.map((h, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, opacity: 0.7 }}>
                    <span style={{ fontFamily: '"Space Mono", monospace' }}>{h.ref}</span>
                    <span>{h.buyer} · +{h.validated_now}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </main>
      ) : (
        <TicketDetail ticket={ticket} onCheckin={checkin} busy={busy} error={error} />
      )}

      {/* Escáner cámara — se monta sobre todo cuando está activo. Al detectar
          un QR cierra la cámara, escribe el token en el input y dispara la
          búsqueda automáticamente (no exige pulsar "Buscar"). */}
      {scannerOpen && (
        <QrCameraScanner
          onScan={(scanned) => {
            setScannerOpen(false);
            setInput(scanned);
            void (async () => {
              setBusy(true);
              setError(null);
              try {
                const r = await api.get<TicketState>(
                  `/operator/entradas/lookup?reference=${encodeURIComponent(scanned)}&t=${encodeURIComponent(token)}`
                );
                setTicket(r.data);
              } catch (e: any) {
                setError(e?.response?.data?.detail || 'No encontrado');
              } finally {
                setBusy(false);
              }
            })();
          }}
          onClose={() => setScannerOpen(false)}
        />
      )}
    </div>
  );
}

function TicketDetail({ ticket, onCheckin, busy, error }: { ticket: TicketState; onCheckin: (n: number) => void; busy: boolean; error: string | null }) {
  const remaining = ticket.quantity - ticket.tickets_validated;
  const isCancelled = ticket.status === 'cancelled';
  const isRefunded = ticket.status === 'refunded';
  const isUsed = remaining <= 0;

  return (
    <main style={{ flex: 1, padding: 24, color: '#F7F2E9', display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ background: 'rgba(255,255,255,0.06)', borderRadius: 20, padding: 22 }}>
        <div style={{ fontFamily: '"Space Mono", monospace', fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', color: '#E8B44A' }}>
          {isCancelled || isRefunded ? 'Bloqueada' : isUsed ? 'Totalmente usada' : 'Vigente'}
        </div>
        <div style={{ fontFamily: '"Spectral", serif', fontSize: 26, fontWeight: 700, marginTop: 6 }}>{ticket.show_title || 'Evento'}</div>
        <div style={{ fontSize: 14, opacity: 0.75, marginTop: 2 }}>
          {ticket.buyer_name} · <span style={{ fontFamily: '"Space Mono", monospace', fontSize: 12 }}>{ticket.order_reference}</span>
        </div>
        {ticket.show_datetime && (
          <div style={{ fontSize: 13, opacity: 0.6, marginTop: 6 }}>
            {new Date(ticket.show_datetime).toLocaleString('es-ES', { dateStyle: 'medium', timeStyle: 'short' })}
            {ticket.venue_name ? ` · ${ticket.venue_name}` : ''}
          </div>
        )}
        <div style={{ marginTop: 20, fontFamily: '"Spectral", serif', fontSize: 44, fontWeight: 700 }}>
          {ticket.tickets_validated} / {ticket.quantity}
        </div>
        <div style={{ fontSize: 13, opacity: 0.65 }}>validadas · restan <b>{Math.max(0, remaining)}</b></div>
      </div>

      {error && <div style={errBox}>{error}</div>}

      {!isCancelled && !isRefunded && !isUsed && (
        remaining === 1 ? (
          /* Solo queda 1: botón único claro — evita confusión con +2/Todas.
             Patricia reportó que el +1/+2/Todas era ruido en compras de 1. */
          <button
            onClick={() => onCheckin(1)}
            disabled={busy}
            style={{ ...btnPrimary, padding: '20px', fontSize: 20 }}
          >Validar entrada</button>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
            <button onClick={() => onCheckin(1)} disabled={busy} style={btnPrimary}>+1</button>
            <button onClick={() => onCheckin(Math.min(2, remaining))} disabled={busy || remaining < 2} style={btnPrimary}>+2</button>
            <button onClick={() => onCheckin(remaining)} disabled={busy} style={btnPrimary}>Todas ({remaining})</button>
          </div>
        )
      )}
    </main>
  );
}

const fullscreenStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: '#0F1517',
  display: 'flex',
  flexDirection: 'column',
  fontFamily: 'system-ui, -apple-system, sans-serif',
  overflow: 'auto',
};

const btnPrimary: React.CSSProperties = {
  background: '#22C55E',
  color: '#0F1517',
  border: 'none',
  padding: '18px',
  borderRadius: 12,
  fontSize: 18,
  fontWeight: 800,
  cursor: 'pointer',
};

const btnGhostSmall: React.CSSProperties = {
  background: 'transparent',
  color: '#F7F2E9',
  border: '1px solid rgba(247,242,233,0.3)',
  padding: '6px 12px',
  borderRadius: 8,
  fontSize: 13,
  cursor: 'pointer',
};

const btnInstall: React.CSSProperties = {
  background: '#E8B44A',
  color: '#0F1517',
  border: 'none',
  padding: '6px 12px',
  borderRadius: 8,
  fontSize: 13,
  fontWeight: 700,
  cursor: 'pointer',
};

const iosHintStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  background: 'rgba(232,180,74,0.1)',
  color: '#F7F2E9',
  padding: '10px 14px',
  borderBottom: '1px solid rgba(232,180,74,0.25)',
  fontSize: 13,
};

const errBox: React.CSSProperties = {
  background: 'rgba(180,69,40,0.2)',
  color: '#F0BA9F',
  padding: 12,
  borderRadius: 10,
  fontSize: 14,
};
